import classNames from 'classnames';
import React, {ReactElement, useCallback} from 'react';

import Row from '../../common/Row';
import {SimpleValue} from '../decision/types';
import Select from './Select';

type Props<T extends SimpleValue> = Pick<
  React.ComponentProps<typeof Select<T>>,
  'options' | 'selectedValues' | 'className' | 'asModal' | 'heading'
> & {
  onChange: (selectedValues: T[], lastAction?: {value: T; selected: boolean}) => void;
  noSelectionLabel?: string;
  withClearAll?: boolean;
  customLabelMaker?: (selectedValues: T[]) => string;
  customCountMaker?: (selectedValues: T[]) => number;
};

const MultiSelect = <T extends SimpleValue>({
  options,
  selectedValues,
  className,
  asModal,
  heading,
  onChange,
  noSelectionLabel,
  withClearAll,
  customLabelMaker,
  customCountMaker,
}: Props<T>): ReactElement => {
  const selectedNames = customLabelMaker
    ? customLabelMaker(selectedValues)
    : options
        .filter(option => selectedValues.includes(option.value))
        .map(option => option.label)
        .join(', ');

  const count = customCountMaker ? customCountMaker(selectedValues) : selectedValues.length;

  const handleChange = useCallback(
    (value: T) => {
      if (selectedValues.includes(value)) {
        onChange(
          selectedValues.filter(v => v !== value),
          {value, selected: false}
        );
      } else {
        onChange([...selectedValues, value].sort(), {value, selected: true});
      }
    },
    [onChange, selectedValues]
  );

  const handleClear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <Select
      buttonContent={
        selectedNames ? (
          <Row className={'grow min-w-0 gap-2'}>
            <span className={'grow min-w-0 text-left whitespace-nowrap overflow-hidden overflow-ellipsis'}>
              {selectedNames}
            </span>
            <span className={'flex-none w-[2rem] h-[2rem] rounded-full bg-primary-500 text-white text-xs'}>
              {count}
            </span>
          </Row>
        ) : (
          <span className={'text-gray-200'}>{noSelectionLabel ?? 'Select options'}</span>
        )
      }
      options={options}
      onOptionSelected={handleChange}
      selectedValues={selectedValues}
      disableSelected={false}
      accessory={'checkbox'}
      className={classNames(selectedNames && '[&>button]:border-primary-700', className)}
      menuClassName={'max-h-[35rem]'}
      floating
      asModal={asModal}
      autoclose={false}
      onClearAll={withClearAll ? handleClear : undefined}
      heading={heading}
    />
  );
};

export default MultiSelect;
